import React, { useEffect, useState } from 'react';
import { 
  FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaYoutube, FaWhatsapp, 
  FaTelegram, FaSnapchat, FaPinterest, FaTiktok, FaDiscord, FaReddit,
  FaGlobe, FaGithub, FaSpotify, FaThreads
} from 'react-icons/fa6';
import { Phone, MapPin } from 'lucide-react';

/**
 * Cosmic4KDVCTemplate - Premium 4K DVC Template with Cyberpunk Cityscape
 * Features: Floating 3D social icons, holographic profile frame, neon light beams
 */
const Cosmic4KDVCTemplate = ({ 
  card, 
  variant = 'galaxy',
  className = '' 
}) => {
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);

  // Social icon components with 3D style colors
  const socialIcons = {
    facebook: { Icon: FaFacebook, color: '#1877F2', bg: 'linear-gradient(135deg, #1877F2, #0d5bc4)' },
    twitter: { Icon: FaTwitter, color: '#000000', bg: 'linear-gradient(135deg, #1DA1F2, #0d8bd9)' },
    instagram: { Icon: FaInstagram, color: '#E4405F', bg: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' },
    linkedin: { Icon: FaLinkedin, color: '#0A66C2', bg: 'linear-gradient(135deg, #0A66C2, #004182)' },
    youtube: { Icon: FaYoutube, color: '#FF0000', bg: 'linear-gradient(135deg, #FF0000, #cc0000)' },
    whatsapp: { Icon: FaWhatsapp, color: '#25D366', bg: 'linear-gradient(135deg, #25D366, #128C7E)' },
    telegram: { Icon: FaTelegram, color: '#0088cc', bg: 'linear-gradient(135deg, #0088cc, #006699)' },
    snapchat: { Icon: FaSnapchat, color: '#000000', bg: 'linear-gradient(135deg, #FFFC00, #f7e100)' },
    pinterest: { Icon: FaPinterest, color: '#FFFFFF', bg: 'linear-gradient(135deg, #E60023, #bd001b)' },
    tiktok: { Icon: FaTiktok, color: '#FFFFFF', bg: 'linear-gradient(135deg, #000000, #25F4EE, #FE2C55)' },
    discord: { Icon: FaDiscord, color: '#FFFFFF', bg: 'linear-gradient(135deg, #5865F2, #4752c4)' },
    reddit: { Icon: FaReddit, color: '#FFFFFF', bg: 'linear-gradient(135deg, #FF4500, #cc3700)' },
    threads: { Icon: FaThreads, color: '#FFFFFF', bg: 'linear-gradient(135deg, #000000, #333333)' },
    spotify: { Icon: FaSpotify, color: '#FFFFFF', bg: 'linear-gradient(135deg, #1DB954, #169c46)' },
    github: { Icon: FaGithub, color: '#FFFFFF', bg: 'linear-gradient(135deg, #181717, #333333)' },
    website: { Icon: FaGlobe, color: '#FFFFFF', bg: 'linear-gradient(135deg, #6366F1, #4f46e5)' },
  };

  // All platforms for floating display
  const allPlatforms = [
    'tiktok', 'linkedin', 'pinterest', 'twitter', 'instagram', 
    'facebook', 'whatsapp', 'youtube', 'snapchat', 'reddit', 
    'discord', 'telegram', 'spotify', 'github'
  ];

  // 3D floating icon positions - scattered around the card like in the reference image
  const iconPositions = [
    { top: '5%', left: '5%', size: 52, delay: 0 },      // TikTok - top left
    { top: '12%', left: '22%', size: 60, delay: 0.2 },  // LinkedIn - large, left area
    { top: '8%', left: '42%', size: 40, delay: 0.4 },   // X/Twitter - top center
    { top: '5%', right: '35%', size: 48, delay: 0.6 },  // TikTok - top center-right
    { top: '8%', right: '22%', size: 44, delay: 0.8 },  // Pinterest - top right area
    { top: '15%', right: '5%', size: 56, delay: 1 },    // Facebook - large, right
    { top: '35%', left: '3%', size: 50, delay: 1.2 },   // Instagram - left side
    { top: '40%', right: '3%', size: 52, delay: 1.4 },  // WhatsApp - right side
    { top: '55%', left: '5%', size: 44, delay: 1.6 },   // Pinterest - lower left
    { top: '50%', right: '5%', size: 48, delay: 1.8 },  // Reddit - lower right
    { top: '70%', left: '10%', size: 56, delay: 2 },    // YouTube - bottom left
    { top: '65%', right: '10%', size: 50, delay: 2.2 }, // Snapchat - bottom right
    { top: '28%', left: '12%', size: 38, delay: 2.4 },  // Discord - small
    { top: '30%', right: '12%', size: 36, delay: 2.6 }, // Telegram - small
  ];

  // Get social links from card
  const socialLinks = card?.social_links || [];
  
  // Function to check if platform is linked
  const getPlatformLink = (platform) => {
    return socialLinks.find(s => s.platform === platform);
  };

  return (
    <div 
      className={`relative w-full overflow-hidden rounded-2xl ${className}`}
      style={{
        aspectRatio: '1/1',
        minHeight: '500px',
        background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a2e 30%, #0d1a2e 60%, #0a0a1a 100%)',
      }}
    >
      {/* Cyberpunk Cityscape Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* City Skyline Silhouette */}
        <svg className="absolute bottom-0 left-0 w-full h-3/4 opacity-40" viewBox="0 0 1000 600" preserveAspectRatio="xMidYMax slice">
          {/* Buildings */}
          <defs>
            <linearGradient id="buildingGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#2a1f4e" />
              <stop offset="100%" stopColor="#0a0a1a" />
            </linearGradient>
            <linearGradient id="neonGlow" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#00ffff" stopOpacity="0" />
              <stop offset="50%" stopColor="#00ffff" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#00ffff" stopOpacity="0" />
            </linearGradient>
          </defs>
          
          {/* Background buildings */}
          <rect x="50" y="200" width="60" height="400" fill="url(#buildingGrad)" />
          <rect x="130" y="150" width="80" height="450" fill="url(#buildingGrad)" />
          <rect x="230" y="250" width="50" height="350" fill="url(#buildingGrad)" />
          <rect x="300" y="100" width="100" height="500" fill="url(#buildingGrad)" />
          <rect x="420" y="180" width="70" height="420" fill="url(#buildingGrad)" />
          <rect x="510" y="80" width="120" height="520" fill="url(#buildingGrad)" />
          <rect x="650" y="200" width="60" height="400" fill="url(#buildingGrad)" />
          <rect x="730" y="130" width="90" height="470" fill="url(#buildingGrad)" />
          <rect x="840" y="220" width="70" height="380" fill="url(#buildingGrad)" />
          <rect x="920" y="160" width="80" height="440" fill="url(#buildingGrad)" />
          
          {/* Neon lights on buildings */}
          <rect x="310" y="110" width="80" height="3" fill="#ff00ff" opacity="0.8" />
          <rect x="520" y="90" width="100" height="3" fill="#00ffff" opacity="0.8" />
          <rect x="740" y="140" width="70" height="3" fill="#ff6600" opacity="0.8" />
          <rect x="140" y="160" width="60" height="3" fill="#00ff00" opacity="0.8" />
        </svg>

        {/* Neon Light Beams - Diagonal streaks */}
        <div 
          className="absolute w-[200%] h-2 opacity-60"
          style={{
            top: '20%',
            left: '-50%',
            background: 'linear-gradient(90deg, transparent, #00ffff, #ff00ff, transparent)',
            transform: 'rotate(-15deg)',
            animation: mounted ? 'lightBeam 4s ease-in-out infinite' : 'none',
            filter: 'blur(2px)',
          }}
        />
        <div 
          className="absolute w-[200%] h-1 opacity-40"
          style={{
            top: '40%',
            left: '-50%',
            background: 'linear-gradient(90deg, transparent, #ff6600, #ff00ff, transparent)',
            transform: 'rotate(-12deg)',
            animation: mounted ? 'lightBeam 5s ease-in-out infinite 1s' : 'none',
            filter: 'blur(1px)',
          }}
        />
        <div 
          className="absolute w-[200%] h-1.5 opacity-50"
          style={{
            top: '60%',
            left: '-50%',
            background: 'linear-gradient(90deg, transparent, #00ff00, #00ffff, transparent)',
            transform: 'rotate(-18deg)',
            animation: mounted ? 'lightBeam 6s ease-in-out infinite 2s' : 'none',
            filter: 'blur(1px)',
          }}
        />

        {/* Additional ambient glow effects */}
        <div 
          className="absolute w-64 h-64 rounded-full opacity-30"
          style={{
            top: '-10%',
            left: '-10%',
            background: 'radial-gradient(circle, #ff00ff 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
        />
        <div 
          className="absolute w-64 h-64 rounded-full opacity-30"
          style={{
            top: '-10%',
            right: '-10%',
            background: 'radial-gradient(circle, #00ffff 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
        />
        <div 
          className="absolute w-48 h-48 rounded-full opacity-20"
          style={{
            bottom: '20%',
            left: '10%',
            background: 'radial-gradient(circle, #00ff00 0%, transparent 70%)',
            filter: 'blur(30px)',
          }}
        />

        {/* Digital circuit patterns */}
        <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" preserveAspectRatio="none">
          <pattern id="circuit" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M0 10 H8 V5 H12 V10 H20" fill="none" stroke="#00ffff" strokeWidth="0.3" />
            <path d="M10 0 V8 H15 V12 H10 V20" fill="none" stroke="#ff00ff" strokeWidth="0.3" />
            <circle cx="10" cy="10" r="1" fill="#00ffff" />
          </pattern>
          <rect width="100" height="100" fill="url(#circuit)" />
        </svg>
      </div>

      {/* 4K DVC Badge - Top Right */}
      <div className="absolute top-4 right-4 z-30 flex items-center gap-1 px-3 py-1.5 rounded-lg"
        style={{
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 215, 0, 0.5)',
          boxShadow: '0 0 20px rgba(255, 215, 0, 0.3)',
        }}
      >
        <span className="text-amber-400 font-black text-sm">4K</span>
        <span className="text-white/80 font-bold text-xs">DVC</span>
      </div>

      {/* Floating 3D Social Icons */}
      {allPlatforms.map((platform, index) => {
        if (index >= iconPositions.length) return null;
        
        const { Icon } = socialIcons[platform] || {};
        const iconData = socialIcons[platform];
        const position = iconPositions[index];
        const link = getPlatformLink(platform);
        const isLinked = !!link;
        
        if (!Icon) return null;
        
        return (
          <a
            key={platform}
            href={link?.url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className={`absolute flex items-center justify-center transition-all duration-500 z-20
              ${isLinked ? 'opacity-100 hover:scale-125 cursor-pointer' : 'opacity-30 pointer-events-none'}
            `}
            style={{
              ...position,
              width: position.size,
              height: position.size,
              borderRadius: '16px',
              background: iconData?.bg || '#333',
              boxShadow: isLinked 
                ? `0 8px 32px ${iconData?.color}60, 0 0 60px ${iconData?.color}30, inset 0 1px 0 rgba(255,255,255,0.2)`
                : '0 4px 15px rgba(0,0,0,0.3)',
              animation: mounted && isLinked ? `float3D ${3 + index * 0.3}s ease-in-out infinite` : 'none',
              animationDelay: `${position.delay}s`,
              transform: 'perspective(500px) rotateY(-5deg) rotateX(5deg)',
            }}
          >
            <Icon 
              className="text-white" 
              style={{ 
                fontSize: position.size * 0.5,
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
              }} 
            />
            
            {/* LINK Label for linked platforms */}
            {isLinked && (
              <div 
                className="absolute -top-3 -right-1 px-1.5 py-0.5 rounded text-[8px] font-mono font-bold"
                style={{
                  background: 'rgba(0, 255, 255, 0.2)',
                  border: '1px solid rgba(0, 255, 255, 0.5)',
                  color: '#00ffff',
                  textShadow: '0 0 10px #00ffff',
                }}
              >
                LINK
              </div>
            )}

            {/* Connection line effect */}
            {isLinked && (
              <div 
                className="absolute w-16 h-px opacity-50"
                style={{
                  left: '100%',
                  top: '50%',
                  background: 'linear-gradient(90deg, #00ffff, transparent)',
                  animation: mounted ? 'pulse 2s ease-in-out infinite' : 'none',
                }}
              />
            )}
          </a>
        );
      })}

      {/* Central Holographic Profile Frame */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-25">
        {/* Outer holographic glow rings */}
        <div 
          className="absolute -inset-8 rounded-3xl"
          style={{
            background: 'linear-gradient(45deg, rgba(0,255,255,0.1), rgba(255,0,255,0.1), rgba(0,255,255,0.1))',
            filter: 'blur(20px)',
            animation: mounted ? 'holoGlow 3s ease-in-out infinite' : 'none',
          }}
        />
        
        {/* Scanning line effect */}
        <div 
          className="absolute inset-0 overflow-hidden rounded-2xl"
          style={{ zIndex: 5 }}
        >
          <div 
            className="absolute w-full h-1 opacity-50"
            style={{
              background: 'linear-gradient(90deg, transparent, #00ffff, transparent)',
              animation: mounted ? 'scanLine 2s linear infinite' : 'none',
              boxShadow: '0 0 20px #00ffff',
            }}
          />
        </div>

        {/* Main holographic frame */}
        <div 
          className="relative rounded-2xl overflow-hidden"
          style={{
            width: '160px',
            height: '200px',
            border: '3px solid transparent',
            borderImage: 'linear-gradient(135deg, #00ffff, #ff00ff, #00ffff) 1',
            boxShadow: `
              0 0 30px rgba(0, 255, 255, 0.4),
              0 0 60px rgba(255, 0, 255, 0.2),
              inset 0 0 40px rgba(0, 255, 255, 0.1)
            `,
          }}
        >
          {/* Inner border glow */}
          <div 
            className="absolute inset-0 rounded-xl pointer-events-none"
            style={{
              border: '2px solid rgba(0, 255, 255, 0.3)',
              boxShadow: 'inset 0 0 20px rgba(0, 255, 255, 0.2)',
            }}
          />
          
          {/* Profile Image or Placeholder */}
          {card?.profile_image ? (
            <img 
              src={card.profile_image} 
              alt={card?.display_name || 'Profile'}
              className="w-full h-full object-cover"
              style={{
                filter: 'brightness(1.1) contrast(1.05)',
              }}
            />
          ) : (
            <div 
              className="w-full h-full flex items-center justify-center"
              style={{
                background: 'linear-gradient(180deg, #1a1a3e 0%, #0a0a1a 100%)',
              }}
            >
              <span 
                className="text-7xl font-bold"
                style={{
                  color: '#00ffff',
                  textShadow: '0 0 30px #00ffff',
                }}
              >
                {card?.display_name?.[0]?.toUpperCase() || '?'}
              </span>
            </div>
          )}

          {/* Holographic overlay effect */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(135deg, rgba(0,255,255,0.05) 0%, transparent 50%, rgba(255,0,255,0.05) 100%)',
            }}
          />

          {/* Grid pattern overlay */}
          <div 
            className="absolute inset-0 pointer-events-none opacity-20"
            style={{
              backgroundImage: `
                linear-gradient(rgba(0,255,255,0.3) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0,255,255,0.3) 1px, transparent 1px)
              `,
              backgroundSize: '20px 20px',
            }}
          />
        </div>

        {/* Corner accent lights */}
        <div className="absolute -top-2 -left-2 w-4 h-4 rounded-full" 
          style={{ 
            background: '#00ffff', 
            boxShadow: '0 0 15px #00ffff, 0 0 30px #00ffff',
            animation: mounted ? 'twinkle 1.5s ease-in-out infinite' : 'none',
          }} 
        />
        <div className="absolute -top-2 -right-2 w-4 h-4 rounded-full" 
          style={{ 
            background: '#ff00ff', 
            boxShadow: '0 0 15px #ff00ff, 0 0 30px #ff00ff',
            animation: mounted ? 'twinkle 1.5s ease-in-out infinite 0.5s' : 'none',
          }} 
        />
        <div className="absolute -bottom-2 -left-2 w-4 h-4 rounded-full" 
          style={{ 
            background: '#ff00ff', 
            boxShadow: '0 0 15px #ff00ff, 0 0 30px #ff00ff',
            animation: mounted ? 'twinkle 1.5s ease-in-out infinite 1s' : 'none',
          }} 
        />
        <div className="absolute -bottom-2 -right-2 w-4 h-4 rounded-full" 
          style={{ 
            background: '#00ffff', 
            boxShadow: '0 0 15px #00ffff, 0 0 30px #00ffff',
            animation: mounted ? 'twinkle 1.5s ease-in-out infinite 1.5s' : 'none',
          }} 
        />
      </div>

      {/* Name and Title - Below profile frame */}
      {card?.display_name && (
        <div className="absolute left-0 right-0 text-center z-20" style={{ bottom: '25%' }}>
          <h2 
            className="text-2xl sm:text-3xl font-black mb-1"
            style={{
              color: '#ffffff',
              textShadow: '0 0 20px rgba(0, 255, 255, 0.8), 0 0 40px rgba(255, 0, 255, 0.5)',
              letterSpacing: '2px',
            }}
          >
            {card.display_name}
          </h2>
          {card.tagline && (
            <p 
              className="text-sm"
              style={{
                color: '#00ffff',
                textShadow: '0 0 10px #00ffff',
                letterSpacing: '1px',
              }}
            >
              {card.tagline}
            </p>
          )}
        </div>
      )}

      {/* Contact Info Bar - Bottom */}
      <div className="absolute bottom-4 left-4 right-4 z-30">
        <div 
          className="flex flex-wrap justify-center gap-6 p-4 rounded-xl"
          style={{
            background: 'linear-gradient(135deg, rgba(0, 30, 60, 0.8), rgba(30, 0, 60, 0.8))',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(0, 255, 255, 0.3)',
            boxShadow: '0 0 30px rgba(0, 255, 255, 0.2), inset 0 0 20px rgba(0, 255, 255, 0.05)',
          }}
        >
          {/* Phone/Call */}
          {card?.show_phone && card?.phone && (
            <a 
              href={`tel:${card.phone}`} 
              className="flex items-center gap-3 transition-all hover:scale-105"
            >
              <div 
                className="p-2 rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.2), rgba(0, 255, 255, 0.1))',
                  boxShadow: '0 0 15px rgba(0, 255, 255, 0.3)',
                }}
              >
                <Phone className="w-5 h-5" style={{ color: '#00ffff' }} />
              </div>
              <div>
                <span 
                  className="text-xs font-bold tracking-wider block"
                  style={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  CALL:
                </span>
                <span 
                  className="text-sm font-semibold"
                  style={{ color: '#ffffff', textShadow: '0 0 10px rgba(255,255,255,0.3)' }}
                >
                  {card.phone}
                </span>
              </div>
            </a>
          )}
          
          {/* Address/Location */}
          {card?.address && (
            <div className="flex items-center gap-3">
              <div 
                className="p-2 rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, rgba(255, 0, 255, 0.2), rgba(255, 0, 255, 0.1))',
                  boxShadow: '0 0 15px rgba(255, 0, 255, 0.3)',
                }}
              >
                <MapPin className="w-5 h-5" style={{ color: '#ff00ff' }} />
              </div>
              <div>
                <span 
                  className="text-xs font-bold tracking-wider block"
                  style={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  ADDRESS:
                </span>
                <span 
                  className="text-sm font-semibold truncate max-w-[180px] block"
                  style={{ color: '#ffffff', textShadow: '0 0 10px rgba(255,255,255,0.3)' }}
                >
                  {card.address}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes float3D {
          0%, 100% { 
            transform: perspective(500px) rotateY(-5deg) rotateX(5deg) translateY(0px);
          }
          50% { 
            transform: perspective(500px) rotateY(5deg) rotateX(-5deg) translateY(-15px);
          }
        }
        
        @keyframes lightBeam {
          0%, 100% { 
            opacity: 0.3;
            transform: rotate(-15deg) translateX(-20%);
          }
          50% { 
            opacity: 0.7;
            transform: rotate(-15deg) translateX(20%);
          }
        }
        
        @keyframes holoGlow {
          0%, 100% { 
            opacity: 0.5;
            filter: blur(20px) hue-rotate(0deg);
          }
          50% { 
            opacity: 0.8;
            filter: blur(25px) hue-rotate(30deg);
          }
        }
        
        @keyframes scanLine {
          0% { top: -10%; }
          100% { top: 110%; }
        }
        
        @keyframes twinkle {
          0%, 100% { 
            opacity: 0.6;
            transform: scale(1);
          }
          50% { 
            opacity: 1;
            transform: scale(1.3);
          }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
};

export default Cosmic4KDVCTemplate;
